// frontend/src/components/notifications/UserNotificationPreferences.js
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../../axiosConfig';
import {
    Clock, Moon, Save, RotateCcw,
    Settings, CheckCircle, XCircle, AlertCircle, Globe, Monitor
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedButton, ThemedCard, ThemedInput } from '../themed';
import ResetNotificationPreferencesDialog from '../dialogs/ResetNotificationPreferencesDialog';
import { usePreferences } from '../../contexts/PreferenceContext';
import referenceService from '../../services/referenceService';
import { getNotificationChannelIcon } from '../../utils/notificationChannelMetadata';
import TimeInput from '../common/TimeInput';
import {
    isBrowserNotificationSupported,
    getBrowserNotificationPermission,
    requestBrowserNotificationPermission,
    sendTestBrowserNotification
} from '../../services/WebSocketService';

/**
 * User notification preferences component
 * Allows users to configure their notification settings per event type and channel
 */
const UserNotificationPreferences = ({ entityId, availableChannels, eventTypes, onSettingsChange }) => {
    const { t } = useTranslation();
    const { background, text, cn } = useTheme();
    const { userPreferences, entitySettings } = usePreferences();
    const userLanguage = userPreferences?.language || 'en';
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [preferences, setPreferences] = useState([]);
    const [originalPreferences, setOriginalPreferences] = useState([]); // Track original state for change detection
    const [globalQuietHours, setGlobalQuietHours] = useState({
        enabled: false,
        start: '22:00',
        end: '07:00'
    });
    const [originalGlobalQuietHours, setOriginalGlobalQuietHours] = useState({
        enabled: false,
        start: '22:00',
        end: '07:00'
    }); // Track original quiet hours
    const [hasChanges, setHasChanges] = useState(false);
    const [notificationLanguage, setNotificationLanguage] = useState('');
    const [originalNotificationLanguage, setOriginalNotificationLanguage] = useState(''); // Track original language
    const [languages, setLanguages] = useState([]);
    const [showResetDialog, setShowResetDialog] = useState(false);
    
    // Entity settings
    const entityDefaultLanguage = entitySettings?.entity_default_language || 'en';
    const multiLanguageEnabled = entitySettings?.multi_language_enabled === true || entitySettings?.multi_language_enabled === 1;
    
    // Check if we should show language selection (only when languages differ and multi-language is enabled)
    const showLanguageSelection = multiLanguageEnabled && userLanguage && entityDefaultLanguage && userLanguage !== entityDefaultLanguage;
    
    useEffect(() => {
        loadUserPreferences();
        loadLanguages();
    }, [entityId]);

    /**
     * Load available languages from reference service
     */
    const loadLanguages = async () => {
        try {
            const langs = await referenceService.getLanguages();
            setLanguages(langs);
        } catch (error) {
            console.error('Error loading languages:', error);
        }
    };

    /**
     * Load user notification preferences
     */
    const loadUserPreferences = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/notification-settings/${entityId}/preferences`);
            console.log("notification preferences response", response);
            if (response.data.success) {
                const prefs = response.data.data;
                setPreferences(prefs);
                setOriginalPreferences(JSON.parse(JSON.stringify(prefs))); // Deep copy for comparison

                // Extract global quiet hours from first preference (if any)
                const firstPref = prefs.find(p => p.quietHoursStart && p.quietHoursEnd);
                const quietHours = firstPref ? {
                    enabled: true,
                    start: firstPref.quietHoursStart,
                    end: firstPref.quietHoursEnd
                } : {
                    enabled: false,
                    start: '22:00',
                    end: '07:00'
                };

                setGlobalQuietHours(quietHours);
                setOriginalGlobalQuietHours(JSON.parse(JSON.stringify(quietHours))); // Deep copy
            }

            // Load notification language preference separately
            try {
                const langResponse = await axios.get(`/api/notification-settings/${entityId}/notification-language`);
                if (langResponse.data.success) {
                    const notifLang = langResponse.data.data.language || userLanguage || entityDefaultLanguage;
                    setNotificationLanguage(notifLang);
                    setOriginalNotificationLanguage(notifLang);
                } else {
                    // Fallback to user or entity language
                    const notifLang = userLanguage || entityDefaultLanguage;
                    setNotificationLanguage(notifLang);
                    setOriginalNotificationLanguage(notifLang);
                }
            } catch (langError) {
                console.log('Notification language not set, using default:', langError.message);
                // Fallback to user or entity language
                const notifLang = userLanguage || entityDefaultLanguage;
                setNotificationLanguage(notifLang);
                setOriginalNotificationLanguage(notifLang);
            }
        } catch (error) {
            console.error('Error loading user preferences:', error);
            toast.error(t('errorLoadingPreferences'));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Check if a preference has changed compared to its original state
     * Only checks event-specific settings (not global quiet hours or language)
     */
    const hasPreferenceChanged = (currentPref, originalPref) => {
        if (!originalPref) return true; // New preference

        // Check only event-specific preference fields for changes
        // Exclude quiet hours and notification language (those are global)
        const fieldsToCheck = [
            'emailEnabled', 'smsEnabled', 'pushEnabled', 'advanceMinutes'
        ];

        return fieldsToCheck.some(field => {
            // Handle null/undefined values consistently
            const current = currentPref[field] ?? null;
            const original = originalPref[field] ?? null;
            return current !== original;
        });
    };

    /**
     * Get only the preferences that have changed
     */
    const getChangedPreferences = () => {
        const changedPrefs = [];

        preferences.forEach(currentPref => {
            const originalPref = originalPreferences.find(op => op.eventTypeId === currentPref.eventTypeId);

            if (hasPreferenceChanged(currentPref, originalPref)) {
                changedPrefs.push({
                    eventTypeId: currentPref.eventTypeId,
                    emailEnabled: currentPref.emailEnabled,
                    smsEnabled: currentPref.smsEnabled,
                    pushEnabled: currentPref.pushEnabled,
                    advanceMinutes: currentPref.advanceMinutes
                    // NOTE: quietHoursStart/End are global user preferences, NOT per-event
                    // They should be saved via the separate /quiet-hours endpoint
                    // NOTE: notificationLanguage is also global, NOT per-event
                });
            }
        });

        return changedPrefs;
    };

    /**
     * Handle channel toggle for specific event type
     */
    const handleChannelToggle = (eventTypeId, channel, enabled) => {
        setPreferences(prev => prev.map(pref => {
            if (pref.eventTypeId === eventTypeId) {
                return {
                    ...pref,
                    [`${channel}Enabled`]: enabled
                };
            }
            return pref;
        }));
        setHasChanges(true);
    };

    /**
     * Handle advance time change
     */
    const handleAdvanceTimeChange = (eventTypeId, minutes) => {
        console.log(`UserNotificationPreferences: Advance time changed for event ${eventTypeId} to ${minutes} minutes`);
        setPreferences(prev => prev.map(pref => {
            if (pref.eventTypeId === eventTypeId) {
                const newAdvanceMinutes = parseInt(minutes) || null;
                console.log(`UserNotificationPreferences: Setting advanceMinutes to ${newAdvanceMinutes}`);
                return {
                    ...pref,
                    advanceMinutes: newAdvanceMinutes
                };
            }
            return pref;
        }));
        setHasChanges(true);
    };

    /**
     * Handle global quiet hours change
     */
    const handleQuietHoursChange = (field, value) => {
        setGlobalQuietHours(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Apply to all preferences
        if (globalQuietHours.enabled) {
            setPreferences(prev => prev.map(pref => ({
                ...pref,
                quietHoursStart: field === 'start' ? value : globalQuietHours.start,
                quietHoursEnd: field === 'end' ? value : globalQuietHours.end
            })));
            setHasChanges(true);
        }
    };

    /**
     * Toggle quiet hours globally
     */
    const handleQuietHoursToggle = (enabled) => {
        setGlobalQuietHours(prev => ({ ...prev, enabled }));
        
        setPreferences(prev => prev.map(pref => ({
            ...pref,
            quietHoursStart: enabled ? globalQuietHours.start : null,
            quietHoursEnd: enabled ? globalQuietHours.end : null
        })));
        setHasChanges(true);
    };

    /**
     * Save preferences - only save changed preferences
     */
    const handleSave = async () => {
        try {
            setSaving(true);

            // Track if anything was actually saved
            let savedSomething = false;

            // 1. Save event-specific preferences
            const changedPreferences = getChangedPreferences();
            if (changedPreferences.length > 0) {
                console.log(`Saving ${changedPreferences.length} changed event preferences:`, changedPreferences);
                console.log('Changed preferences detail:', JSON.stringify(changedPreferences, null, 2));
                await axios.put(`/api/notification-settings/${entityId}/preferences`, changedPreferences);
                savedSomething = true;
            }

            // 2. Save global quiet hours if changed
            const quietHoursChanged =
                globalQuietHours.enabled !== originalGlobalQuietHours.enabled ||
                globalQuietHours.start !== originalGlobalQuietHours.start ||
                globalQuietHours.end !== originalGlobalQuietHours.end;

            if (quietHoursChanged) {
                console.log('Saving changed quiet hours:', globalQuietHours);
                await axios.put(`/api/notification-settings/${entityId}/quiet-hours`, globalQuietHours);
                savedSomething = true;
            }

            // 3. Save notification language if changed
            const languageChanged = notificationLanguage !== originalNotificationLanguage;
            if (languageChanged) {
                console.log('Saving changed notification language:', notificationLanguage);
                await axios.put(`/api/notification-settings/${entityId}/notification-language`, {
                    language: notificationLanguage
                });
                savedSomething = true;
            }

            if (!savedSomething) {
                console.log('No preferences have changed, skipping save operation');
                toast.info(t('noChangesToSave'));
                setHasChanges(false);
                return;
            }

            // Update original states to reflect saved changes
            setOriginalPreferences(JSON.parse(JSON.stringify(preferences)));
            setOriginalGlobalQuietHours(JSON.parse(JSON.stringify(globalQuietHours)));
            setOriginalNotificationLanguage(notificationLanguage);

            toast.success(t('preferencesUpdated'));
            setHasChanges(false);

            if (onSettingsChange) {
                onSettingsChange();
            }
        } catch (error) {
            console.error('Error saving preferences:', error);
            toast.error(t('errorSavingPreferences'));
        } finally {
            setSaving(false);
        }
    };

    /**
     * Reset to defaults
     */
    const handleReset = () => {
        setShowResetDialog(true);
    };

    /**
     * Perform the actual reset after confirmation
     */
    const performReset = async () => {
        try {
            setSaving(true);
            setShowResetDialog(false);
            await axios.delete(`/api/notification-settings/${entityId}/preferences`);

            toast.success(t('preferencesReset'));
            setHasChanges(false);

            // Reload preferences and reset original state tracking
            await loadUserPreferences();

            if (onSettingsChange) {
                onSettingsChange();
            }
        } catch (error) {
            console.error('Error resetting preferences:', error);
            toast.error(t('errorResettingPreferences'));
        } finally {
            setSaving(false);
        }
    };

    /**
     * Check if user has any notifications enabled
     */
    const hasAnyNotificationsEnabled = () => {
        return preferences.some(pref => 
            Object.entries(availableChannels)
                .filter(([channel, isAvailable]) => isAvailable)
                .some(([channel]) => pref[`${channel}Enabled`])
        );
    };

    if (loading) {
        return (
            <div className={cn("flex justify-center items-center h-32", text.secondary)}>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-3">{t('loadingPreferences')}...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className={cn("text-lg font-semibold", text.primary)}>
                        {t('notificationPreferences')}
                    </h2>
                    <p className={cn("text-sm", text.secondary)}>
                        {t('chooseHowYouWantToReceiveNotifications')}
                    </p>
                </div>
                
                <div className="flex items-center space-x-2">
                    {hasChanges && (
                        <span className={cn("text-sm", "text-amber-600 dark:text-amber-400")}>
                            <AlertCircle className="w-4 h-4 inline mr-1" />
                            {t('unsavedChanges')}
                        </span>
                    )}
                    <ThemedButton
                        onClick={handleReset}
                        variant="outline"
                        size="sm"
                        disabled={saving}
                    >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        {t('reset')}
                    </ThemedButton>
                    <ThemedButton
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                        size="sm"
                    >
                        <Save className="w-4 h-4 mr-1" />
                        {saving ? t('saving') : t('savePreferences')}
                    </ThemedButton>
                </div>
            </div>

            {/* Global Settings */}
            <ThemedCard className="p-4">
                <h3 className={cn("text-md font-medium mb-4", text.primary)}>
                    <Settings className="w-4 h-4 inline mr-2" />
                    {t('globalSettings')}
                </h3>
                
                {/* Notification Language - Only show when languages differ and multi-language is enabled */}
                {showLanguageSelection && (
                    <div className="mb-4">
                        <label className={cn("text-sm font-medium block mb-3", text.primary)}>
                            <Globe className="w-4 h-4 inline mr-2" />
                            {t('notificationLanguage')}
                        </label>
                        <div className="space-y-2">
                            {/* User's Language Option */}
                            <label className={cn(
                                "flex items-center p-3 rounded-lg border cursor-pointer transition-colors",
                                notificationLanguage === userLanguage 
                                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" 
                                    : cn("border-gray-200 dark:border-gray-700", background.secondary)
                            )}>
                                <input
                                    type="radio"
                                    name="notificationLanguage"
                                    value={userLanguage}
                                    checked={notificationLanguage === userLanguage}
                                    onChange={(e) => {
                                        setNotificationLanguage(e.target.value);
                                        setHasChanges(true);
                                    }}
                                    className="mr-3 text-blue-600 focus:ring-blue-500"
                                />
                                <div className="flex-1">
                                    <div className={cn("font-medium", text.primary)}>
                                        {languages.find(l => l.language_code === userLanguage)?.language_name || userLanguage.toUpperCase()}
                                    </div>
                                    <div className={cn("text-xs", text.secondary)}>
                                        {t('yourPreference')}
                                    </div>
                                </div>
                                {notificationLanguage === userLanguage && (
                                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                )}
                            </label>
                            
                            {/* Entity Default Language Option */}
                            <label className={cn(
                                "flex items-center p-3 rounded-lg border cursor-pointer transition-colors",
                                notificationLanguage === entityDefaultLanguage 
                                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" 
                                    : cn("border-gray-200 dark:border-gray-700", background.secondary)
                            )}>
                                <input
                                    type="radio"
                                    name="notificationLanguage"
                                    value={entityDefaultLanguage}
                                    checked={notificationLanguage === entityDefaultLanguage}
                                    onChange={(e) => {
                                        setNotificationLanguage(e.target.value);
                                        setHasChanges(true);
                                    }}
                                    className="mr-3 text-blue-600 focus:ring-blue-500"
                                />
                                <div className="flex-1">
                                    <div className={cn("font-medium", text.primary)}>
                                        {languages.find(l => l.language_code === entityDefaultLanguage)?.language_name || entityDefaultLanguage.toUpperCase()}
                                    </div>
                                    <div className={cn("text-xs", text.secondary)}>
                                        {t('organizationDefault')}
                                    </div>
                                </div>
                                {notificationLanguage === entityDefaultLanguage && (
                                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                )}
                            </label>
                        </div>
                        <p className={cn("text-xs mt-2 ml-1", text.secondary)}>
                            {t('chooseLanguageForNotifications')}
                        </p>
                    </div>
                )}

                {/* Browser Notifications */}
                {isBrowserNotificationSupported() && (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <Monitor className={cn("w-5 h-5", text.secondary)} />
                                <div>
                                    <h4 className={cn("text-sm font-medium", text.primary)}>
                                        {t('browserNotifications')}
                                    </h4>
                                    <p className={cn("text-xs", text.secondary)}>
                                        {t('browserNotificationsDescription')}
                                    </p>
                                </div>
                            </div>
                            {getBrowserNotificationPermission() === 'granted' ? (
                                <div className="flex items-center space-x-2">
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                    <span className={cn("text-sm text-green-600 dark:text-green-400")}>
                                        {t('enabled')}
                                    </span>
                                    <ThemedButton
                                        onClick={() => {
                                            if (sendTestBrowserNotification(
                                                t('testNotificationTitle'),
                                                t('testNotificationBody')
                                            )) {
                                                toast.success(t('testNotificationSent'));
                                            }
                                        }}
                                        className="text-xs px-2 py-1"
                                    >
                                        {t('test')}
                                    </ThemedButton>
                                </div>
                            ) : getBrowserNotificationPermission() === 'denied' ? (
                                <div className="flex items-center space-x-2">
                                    <XCircle className="w-5 h-5 text-red-500" />
                                    <span className={cn("text-xs", text.secondary)}>
                                        {t('browserNotificationsBlocked')}
                                    </span>
                                </div>
                            ) : (
                                <ThemedButton
                                    onClick={async () => {
                                        const result = await requestBrowserNotificationPermission();
                                        if (result === 'granted') {
                                            toast.success(t('browserNotificationsEnabled'));
                                            setHasChanges(prev => !prev);
                                        } else if (result === 'denied') {
                                            toast.error(t('browserNotificationsDenied'));
                                        }
                                    }}
                                    className="text-sm"
                                >
                                    {t('enableBrowserNotifications')}
                                </ThemedButton>
                            )}
                        </div>
                        {getBrowserNotificationPermission() === 'denied' && (
                            <p className={cn("text-xs ml-8", text.muted)}>
                                {t('browserNotificationsBlockedHelp')}
                            </p>
                        )}
                    </div>
                )}

                {/* Quiet Hours */}
                <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                        <input
                            type="checkbox"
                            id="quietHours"
                            checked={globalQuietHours.enabled}
                            onChange={(e) => handleQuietHoursToggle(e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="quietHours" className={cn("text-sm font-medium", text.primary)}>
                            <Moon className="w-4 h-4 inline mr-1" />
                            {t('enableQuietHours')}
                        </label>
                    </div>

                    {globalQuietHours.enabled && (
                        <>
                            <div className="flex items-center space-x-4 ml-6">
                                <div className="flex items-center space-x-2">
                                    <label className={cn("text-sm", text.secondary)}>{t('from')}:</label>
                                    <ThemedInput
                                        type="time"
                                        value={globalQuietHours.start}
                                        onChange={(e) => handleQuietHoursChange('start', e.target.value)}
                                        className="w-24"
                                    />
                                </div>
                                <div className="flex items-center space-x-2">
                                    <label className={cn("text-sm", text.secondary)}>{t('to')}:</label>
                                    <ThemedInput
                                        type="time"
                                        value={globalQuietHours.end}
                                        onChange={(e) => handleQuietHoursChange('end', e.target.value)}
                                        className="w-24"
                                    />
                                </div>
                            </div>
                            {/* Timezone clarification */}
                            <div className={cn("ml-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800")}>
                                <div className="flex items-start space-x-2">
                                    <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                                    <div className="space-y-1">
                                        <p className={cn("text-xs font-medium text-blue-800 dark:text-blue-300")}>
                                            {t('quietHoursTimezoneNote')}
                                        </p>
                                        <p className={cn("text-xs text-blue-700 dark:text-blue-400")}>
                                            {entitySettings?.timezone_display
                                                ? `${t('organizationTimezone')}: ${entitySettings.timezone_display} (${entitySettings.timezone_offset})`
                                                : `${t('organizationTimezone')}: ${entitySettings?.time_zone || 'America/Toronto'}`
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    <p className={cn("text-xs ml-6", text.secondary)}>
                        {t('quietHoursDescription')}
                    </p>
                </div>
            </ThemedCard>

            {/* Notification Types */}
            <div className="space-y-4">
                {eventTypes.map(eventType => {
                    const pref = preferences.find(p => p.eventTypeId === eventType.id);
                    if (!pref) return null;

                    return (
                        <ThemedCard key={eventType.id} className="p-4">
                            <div className="space-y-4">
                                {/* Event Type Header */}
                                <div>
                                    <h3 className={cn("text-md font-medium", text.primary)}>
                                        {eventType.displayName}
                                    </h3>
                                    <p className={cn("text-sm", text.secondary)}>
                                        {eventType.description}
                                    </p>
                                </div>

                                {/* Channel Toggles - Support dynamic channels */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {Object.entries(availableChannels).map(([channel, isChannelAvailable]) => {
                                        // Only show channels that are both globally active and entity-enabled
                                        if (!isChannelAvailable) return null;
                                        
                                        const Icon = getNotificationChannelIcon(channel);
                                        const isEnabled = pref[`${channel}Enabled`];
                                        
                                        return (
                                            <div 
                                                key={channel}
                                                className={cn(
                                                    "flex items-center justify-between p-3 rounded-lg border transition-colors",
                                                    isEnabled 
                                                        ? "border-green-300 bg-green-50 dark:border-green-600 dark:bg-green-900/20"
                                                        : cn("border-gray-200 dark:border-gray-700", background.secondary)
                                                )}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <Icon className={cn(
                                                        "w-4 h-4",
                                                        isEnabled ? "text-green-600 dark:text-green-400" : text.secondary
                                                    )} />
                                                    <span className={cn(
                                                        "text-sm font-medium",
                                                        isEnabled ? "text-green-700 dark:text-green-300" : text.primary
                                                    )}>
                                                        {t(channel)}
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => handleChannelToggle(
                                                        eventType.id, 
                                                        channel, 
                                                        !isEnabled
                                                    )}
                                                    className={cn(
                                                        "w-10 h-6 rounded-full transition-colors relative",
                                                        isEnabled 
                                                            ? "bg-green-500" 
                                                            : "bg-gray-300 dark:bg-gray-600"
                                                    )}
                                                >
                                                    <div className={cn(
                                                        "w-4 h-4 rounded-full bg-white transition-transform absolute top-1",
                                                        isEnabled ? "translate-x-5" : "translate-x-1"
                                                    )} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Advance Time Setting */}
                                {eventType.supports_advance_time !== 0 && (
                                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                                        <div className="flex items-center space-x-4">
                                            <Clock className={cn("w-4 h-4", text.secondary)} />
                                            <label className={cn("text-sm font-medium", text.primary)}>
                                                {t('advanceNotice')}:
                                            </label>
                                            <TimeInput
                                                value={pref.advanceMinutes}
                                                onChange={(minutes) => handleAdvanceTimeChange(eventType.id, minutes)}
                                                minMinutes={eventType.min_advance_minutes}
                                                maxMinutes={eventType.max_advance_minutes}
                                                placeholder={eventType.default_advance_minutes?.toString()}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </ThemedCard>
                    );
                })}
            </div>

            {/* Status Summary */}
            <ThemedCard className="p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        {hasAnyNotificationsEnabled() ? (
                            <>
                                <CheckCircle className="w-5 h-5 text-green-500" />
                                <span className={cn("text-sm font-medium", text.primary)}>
                                    {t('notificationsEnabled')}
                                </span>
                            </>
                        ) : (
                            <>
                                <XCircle className="w-5 h-5 text-orange-500" />
                                <span className={cn("text-sm font-medium", text.primary)}>
                                    {t('noNotificationsEnabled')}
                                </span>
                            </>
                        )}
                    </div>
                    <div className={cn("text-xs", text.secondary)}>
                        {preferences.filter(p => p.hasCustomSettings).length} / {preferences.length} {t('customized')}
                    </div>
                </div>
            </ThemedCard>

            {/* Reset Notification Preferences Confirmation Dialog */}
            <ResetNotificationPreferencesDialog
                isOpen={showResetDialog}
                onClose={() => setShowResetDialog(false)}
                onConfirm={performReset}
                isResetting={saving}
                entityName={entitySettings?.name || 'this organization'}
            />
        </div>
    );
};

export default UserNotificationPreferences;
